import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Feature } from 'ol';
import { Coordinate } from 'ol/coordinate';
import Polygon from 'ol/geom/Polygon';
import { Modify, Select } from 'ol/interaction';
import Draw, { DrawEvent } from 'ol/interaction/Draw'; // Import DrawEvent
import Snap from 'ol/interaction/Snap';
import VectorLayer from 'ol/layer/Vector';
import OLMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style, Circle } from 'ol/style';
import { IsavMap } from '../../map';
import { fromMapEvent } from '../../util/from-map-event';

export type DrawMode = 'modify' | 'draw';

@UntilDestroy()
@Component({
    selector: 'isav-map-draw-area',
    templateUrl: 'draw-area.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class IsavMapDrawArea implements OnInit, OnChanges, OnDestroy {
    @Input() disabled: boolean = false;
    @Input() coordinate: Coordinate[] = []; // WGS84 coordinates
    @Output() coordinateChange = new EventEmitter<Coordinate[]>();

    private _active: DrawMode = 'draw';
    private _map: OLMap;
    private _vectorSource: VectorSource<Feature<Polygon>>;
    private _vectorLayer: VectorLayer<Feature<Polygon>>;
    private _draw: Draw;
    private _select: Select;
    private _modify: Modify;
    private _snap: Snap;
    private _polygonStyle: Style;

    constructor(
        private _isavMap: IsavMap,
        private ngZone: NgZone
    ) {
        this._polygonStyle = new Style({
            fill: new Fill({
                color: 'rgba(255, 255, 255, 0.4)',
            }),
            stroke: new Stroke({
                color: '#4099de',
                width: 2,
            }),
            image: new Circle({
                radius: 6,
                fill: new Fill({
                    color: '#4099de',
                }),
            }),
        });
    }

    ngOnInit(): void {
        this.ngZone.runOutsideAngular(() => {
            this._map = this._isavMap.map;

            this._vectorSource = new VectorSource<Feature<Polygon>>();
            this._vectorLayer = new VectorLayer({
                source: this._vectorSource,
                style: this._polygonStyle,
                zIndex: 1,
            });
            this._map.addLayer(this._vectorLayer);

            this._draw = new Draw({
                source: this._vectorSource,
                type: 'Polygon',
                style: this._polygonStyle,
            });

            this._map.addInteraction(this._draw);

            this._modify = new Modify({
                source: this._vectorSource,
                style: this._polygonStyle,
            });
            this._map.addInteraction(this._modify);

            this._select = new Select({
                style: this._polygonStyle,
            });
            this._map.addInteraction(this._select);

            this._snap = new Snap({ source: this._vectorSource });
            this._map.addInteraction(this._snap);

            fromMapEvent(this._draw as any, 'drawend')
                .pipe(untilDestroyed(this))
                .subscribe((event: DrawEvent) => {
                    const feature = event.feature as Feature<Polygon>;
                    if (feature && feature.getGeometry()) {
                        const coordinates = feature.getGeometry()?.getCoordinates()[0] || [];
                        this.ngZone.run(() => {
                            this.coordinate = coordinates;
                            this.coordinateChange.emit(this.coordinate);
                        });

                        // Ensure the feature is added to the source
                        this._vectorSource.clear();
                        this._vectorSource.addFeature(feature);

                        // Disable drawing after polygon is created
                        this._map.removeInteraction(this._draw);

                        // Force a render of the map
                        this._map.render();
                    }
                });

            // Handle modify events
            fromMapEvent(this._modify as any, 'modifyend')
                .pipe(untilDestroyed(this))
                .subscribe(() => {
                    const features = this._vectorSource.getFeatures();
                    if (features.length > 0) {
                        const feature = features[0] as Feature<Polygon>;
                        if (feature && feature.getGeometry()) {
                            const coordinates = feature.getGeometry()?.getCoordinates()[0] || [];
                            this.ngZone.run(() => {
                                this.coordinate = coordinates;
                                this.coordinateChange.emit(this.coordinate);
                            });
                        }
                    }
                });
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('coordinate' in changes && this._vectorSource) {
            this.ngZone.runOutsideAngular(() => {
                this._vectorSource.clear();
                if (this.coordinate && this.coordinate.length) {
                    const feature = new Feature(new Polygon([this.coordinate]));
                    feature.setStyle(this._polygonStyle);
                    this._vectorSource.addFeature(feature);
                    // Force a render of the map
                    this._map.render();
                }
            });
        }
    }

    ngOnDestroy(): void {
        this._map.removeInteraction(this._draw);
        this._map.removeInteraction(this._modify);
        this._map.removeInteraction(this._select);
        this._map.removeInteraction(this._snap);
        this._map.removeLayer(this._vectorLayer);
    }

    clear(): void {
        this._vectorSource.clear();
        this.coordinate = [];
        this.coordinateChange.emit(this.coordinate);
        // Re-enable drawing after polygon is cleared
        this._map.addInteraction(this._draw);
    }
}
